import { Box, IconButton, MenuItem, TextField, Tooltip } from '@mui/material'
import { createFileRoute } from '@tanstack/react-router'
import dayjs from 'dayjs'
import { useMemo, useState } from 'react'
import { CHARGE_STATUS_LABEL, type ChargeStatus, chargeStatuses } from '@/features/dinheiro/api'
import { Cobrancas } from '@/features/dinheiro/Cobrancas'
import { Conciliacao } from '@/features/dinheiro/Conciliacao'
import { Exportacao } from '@/features/dinheiro/Exportacao'
import { monthKey, monthRange, parseMonthKey } from '@/lib/dinheiro'
import NiChevronLeftSmall from '@/ui/icons/nexture/ni-chevron-left-small'
import NiChevronRightSmall from '@/ui/icons/nexture/ni-chevron-right-small'
import { Page } from '@/ui/kit/Page'
import { Panel } from '@/ui/kit/Panel'
import { DatePicker } from '@/ui/kit/pickers'

const TODOS = 'todos'

function ehStatus(valor: string): valor is ChargeStatus {
  return (chargeStatuses as readonly string[]).includes(valor)
}

/**
 * Dinheiro.
 *
 * Três coisas na mesma tela porque são a mesma pergunta em três alturas: o que
 * entrou no mês, o que está divergente e o que a contabilidade precisa. Nada
 * aqui é clínico, então a tela não passa pelo `KeyGate`: o valor de uma sessão
 * não diz nada sobre a pessoa.
 */
function Dinheiro() {
  const [mes, setMes] = useState(() => monthKey(new Date()))
  const [status, setStatus] = useState<ChargeStatus | typeof TODOS>(TODOS)

  const periodo = useMemo(() => parseMonthKey(mes), [mes])
  const filtro = useMemo(() => {
    if (!periodo) return null
    return {
      ...monthRange(periodo.year, periodo.month),
      ...(status === TODOS ? {} : { status }),
    }
  }, [periodo, status])

  const inicio = dayjs(`${mes}-01`)
  const andar = (meses: number) => setMes(inicio.add(meses, 'month').format('YYYY-MM'))

  return (
    <Page
      title="Dinheiro"
      description="Cobranças do mês, o que diverge do provedor e a exportação para a contabilidade."
      actions={
        <Box className="flex flex-wrap items-center gap-2">
          <Box className="flex items-center">
            <Tooltip title="Mês anterior">
              <IconButton className="h-11 w-11" aria-label="Mês anterior" onClick={() => andar(-1)}>
                <NiChevronLeftSmall size="medium" />
              </IconButton>
            </Tooltip>
            <DatePicker
              views={['year', 'month']}
              openTo="month"
              label="Mês"
              format="MMMM [de] YYYY"
              value={inicio.isValid() ? inicio : null}
              onChange={(valor) => {
                if (valor?.isValid()) setMes(valor.format('YYYY-MM'))
              }}
              slotProps={{ textField: { id: 'filtro-mes', className: 'w-52', size: 'small' } }}
            />
            <Tooltip title="Próximo mês">
              <IconButton className="h-11 w-11" aria-label="Próximo mês" onClick={() => andar(1)}>
                <NiChevronRightSmall size="medium" />
              </IconButton>
            </Tooltip>
          </Box>
          <TextField
            select
            id="status"
            label="Status"
            size="small"
            className="w-44"
            value={status}
            onChange={(event) =>
              setStatus(ehStatus(event.target.value) ? event.target.value : TODOS)
            }
            slotProps={{
              select: { SelectDisplayProps: { 'data-testid': 'filtro-status' } as object },
            }}
          >
            <MenuItem value={TODOS}>Todos</MenuItem>
            {chargeStatuses.map((item) => (
              <MenuItem key={item} value={item} className="first-letter:uppercase">
                {CHARGE_STATUS_LABEL[item]}
              </MenuItem>
            ))}
          </TextField>
        </Box>
      }
    >
      {filtro !== null && <Cobrancas filter={filtro} />}

      <Box className="grid gap-5 lg:grid-cols-12">
        <Panel
          className="lg:col-span-8"
          title="Conciliação"
          subtitle="O que não bate entre o nosso registro e o do provedor. A conciliação abre a divergência e não corrige nada sozinha."
        >
          <Conciliacao />
        </Panel>
        <Panel
          className="lg:col-span-4 lg:self-start"
          title="Contabilidade"
          subtitle="Uma linha por recibo emitido no mês, no formato que a contabilidade abre."
        >
          {periodo && <Exportacao year={periodo.year} month={periodo.month} />}
        </Panel>
      </Box>
    </Page>
  )
}

export const Route = createFileRoute('/_app/app/dinheiro')({
  staticData: { crumb: { label: 'Dinheiro' } },
  component: Dinheiro,
})
